import { useState } from 'react';
import { BookOpen, CheckCircle2, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { createAgentLoan, createLoan, deleteLoan, uploadAgentLoanDocument, uploadLoanDocument } from '../../../lib/api';
import { getLoanApplicationRoleConfig } from '../../../lib/loanApplicationFlow';
import { buildLoanApplicationPayload, validateLoanApplicationStep, validateLoanApplicationSubmission } from '../../../lib/loanApplicationValidation';
import { toast } from 'sonner';

function Field({ label, required, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}{required ? <span className="text-rose-500 ml-0.5">*</span> : null}
            </Label>
            {children}
        </div>
    );
}

function FormInput({ id, type, label, placeholder, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <Input id={id} type={type || 'text'} placeholder={placeholder} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all" />
        </Field>
    );
}

function FormSelect({ id, label, options, value, onChange, required }) {
    return (
        <Field label={label} required={required}>
            <select id={id} value={value} onChange={onChange} required={required}
                className="h-11 rounded-xl border px-3 text-sm w-full outline-none border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all">
                <option value="">Select…</option>
                {options.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
        </Field>
    );
}

function FileUploadField({ label, accept, onChange, file }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</Label>
            <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed rounded-xl cursor-pointer border-slate-200 dark:border-slate-700 hover:border-amber-400 bg-slate-50 dark:bg-slate-800/50 hover:bg-amber-50/50 transition-all group">
                <input type="file" accept={accept} className="sr-only" onChange={onChange} />
                {file ? (
                    <div className="flex items-center gap-2 px-3"><CheckCircle2 className="h-5 w-5 text-amber-500 shrink-0" /><span className="text-xs text-amber-600 font-medium truncate max-w-[160px]">{file.name}</span></div>
                ) : (
                    <div className="flex flex-col items-center gap-1"><Upload className="h-5 w-5 text-slate-400 group-hover:text-amber-500 transition-colors" /><span className="text-xs text-slate-500">Click to upload</span></div>
                )}
            </label>
        </div>
    );
}

function ProgressBar({ steps, current }) {
    var pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
    return (
        <div className="w-full">
            <div className="flex items-start justify-between mb-6">
                {steps.map(function (step, i) {
                    var done = i < current, active = i === current;
                    var cc = done ? 'bg-amber-500 border-amber-500 text-white' : active ? 'bg-white dark:bg-slate-900 border-amber-500 text-amber-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400';
                    var lc = active ? 'text-amber-600 dark:text-amber-400' : done ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400';
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center relative">
                            {i < steps.length - 1 ? <div className={'absolute top-4 left-1/2 w-full h-0.5 ' + (done ? 'bg-amber-500' : 'bg-slate-200 dark:bg-slate-700')} /> : null}
                            <div className={'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ' + cc}>{done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
                            <span className={'mt-2 text-[10px] font-medium text-center leading-tight max-w-[70px] ' + lc}>{step}</span>
                        </div>
                    );
                })}
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-700" style={{ width: pct + '%' }} />
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">Step {current + 1} of {steps.length}</span>
                <span className="text-[10px] text-amber-600 font-semibold">{pct}% Complete</span>
            </div>
        </div>
    );
}

var STEPS = ['Student Details', 'Co-Applicant', 'Documents', 'Review & Submit'];

const AgentEducationLoan = ({ roleBasePath = '/agent' }) => {
    const roleConfig = getLoanApplicationRoleConfig(roleBasePath);
    const isAgentPortal = roleBasePath === '/agent';
    const createLoanRequest = isAgentPortal ? createAgentLoan : createLoan;
    const uploadLoanDocumentRequest = isAgentPortal ? uploadAgentLoanDocument : uploadLoanDocument;
    var ss = useState({ studentName: '', dob: '', mobile: '', email: '', aadhaar: '', courseName: '', collegeName: '', country: '', courseDuration: '', loanAmount: '' });
    var student = ss[0]; var setStudent = ss[1];
    var sco = useState({ coName: '', relationship: '', coIncome: '' });
    var co = sco[0]; var setCo = sco[1];
    var sf = useState({}); var files = sf[0]; var setFiles = sf[1];
    var sc = useState(0); var current = sc[0]; var setCurrent = sc[1];
    var sub = useState(false); var submitted = sub[0]; var setSubmitted = sub[1];
    const { token } = useAuth();

    function upd(setter) { return function (k, v) { setter(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }; }
    function updFile(k, f) { setFiles(function (p) { var n = Object.assign({}, p); n[k] = f; return n; }); }
    var us = upd(setStudent), uco = upd(setCo);
    function cs(k) { return { value: student[k], onChange: function (e) { us(k, e.target.value); } }; }
    function cc(k) { return { value: co[k], onChange: function (e) { uco(k, e.target.value); } }; }

    var isLast = current === STEPS.length - 1;
    async function handleNext() {
        var state = { student: student, coApplicant: co, files: files };
        if (!isLast) {
            var stepErrors = validateLoanApplicationStep('education', current, state);
            if (Object.keys(stepErrors).length > 0) {
                toast.error(Object.values(stepErrors)[0] || 'Please fix the highlighted fields.');
                return;
            }
        }
        if (isLast) {
            if (!token) {
                toast.error('Please log in to file this loan application.');
                return;
            }
            var validation = validateLoanApplicationSubmission('education', STEPS.length, state);
            if (!validation.success) {
                setCurrent(validation.stepIndex);
                toast.error(Object.values(validation.errors)[0] || 'Please fix the highlighted fields.');
                return;
            }
            let createdLoanId = null;
            try {
                const resp = await createLoanRequest(token, buildLoanApplicationPayload('education', state));
                const loanId = resp?.data?.id;
                createdLoanId = loanId;
                const fileEntries = Object.entries(files || {}).filter(([, f]) => f instanceof File);
                if (loanId && fileEntries.length > 0) {
                    toast.info(`Uploading ${fileEntries.length} document(s)…`);
                    const uploadResults = await Promise.allSettled(
                        fileEntries.map(([key, file]) => uploadLoanDocumentRequest(token, loanId, key, file))
                    );
                    const failedDocKeys = uploadResults.reduce((acc, result, index) => {
                        if (result.status === 'rejected') acc.push(fileEntries[index][0]);
                        return acc;
                    }, []);
                    if (failedDocKeys.length > 0) {
                        const firstFailure = uploadResults.find((result) => result.status === 'rejected');
                        const failureReason = firstFailure?.status === 'rejected' ? firstFailure.reason?.message : '';
                        throw new Error(
                            `Document upload failed for: ${failedDocKeys.join(', ')}${failureReason ? `. ${failureReason}` : ''}`
                        );
                    }
                }
                toast.success('Education loan application filed successfully.');
                setSubmitted(true);
            } catch (err) {
                let message = err?.message || 'Failed to file education loan application';
                if (createdLoanId) {
                    try {
                        await deleteLoan(token, createdLoanId, { rollbackUploadFailure: true });
                        message = `${message}. Created loan was rolled back.`;
                    } catch (rollbackError) {
                        const rollbackMessage = rollbackError?.message || 'Failed to roll back the created loan';
                        message = `${message}. Rollback failed: ${rollbackMessage}`;
                    }
                }
                toast.error(message);
            }
            return;
        }
        setCurrent(function (p) { return p + 1; });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    function handleBack() { setCurrent(function (p) { return p - 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }

    var docs = [{ key: 'studentId', label: 'Student ID / Marksheet', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'admission', label: 'Admission Letter', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'feeStructure', label: 'Fee Structure', accept: '.pdf' }, { key: 'panCard', label: 'PAN Card (Co-Applicant)', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'aadhaarCard', label: 'Aadhaar (Co-Applicant)', accept: '.pdf,.jpg,.jpeg,.png' }, { key: 'incomeProof', label: 'Income Proof of Co-Applicant', accept: '.pdf' }, { key: 'bankStatement', label: 'Bank Statement (6 Months)', accept: '.pdf' }];

    var stepContent;
    if (current === 0) {
        stepContent = (<div className="grid gap-5 sm:grid-cols-2"><FormInput id="sn" label="Student Name" placeholder="Ananya Verma" required {...cs('studentName')} /><FormInput id="db" label="Date of Birth" type="date" required {...cs('dob')} /><FormInput id="mo" label="Mobile Number" type="tel" placeholder="+91 98765 43210" required {...cs('mobile')} /><FormInput id="em" label="Email" type="email" placeholder="ananya@example.com" required {...cs('email')} /><FormInput id="aa" label="Aadhaar Number" placeholder="1234 5678 9012" required {...cs('aadhaar')} /><FormInput id="cn" label="Course Name" placeholder="B.Tech Computer Science" required {...cs('courseName')} /><FormInput id="col" label="College / University" placeholder="IIT Bombay" required {...cs('collegeName')} /><FormSelect id="cty" label="Country of Study" required options={[{ value: 'india', label: 'India' }, { value: 'usa', label: 'USA' }, { value: 'uk', label: 'UK' }, { value: 'canada', label: 'Canada' }, { value: 'australia', label: 'Australia' }, { value: 'other', label: 'Other' }]} {...cs('country')} /><FormInput id="cd" label="Course Duration" placeholder="4 years" required {...cs('courseDuration')} /><FormInput id="la" label="Loan Amount Required (Rs.)" type="number" placeholder="1500000" required {...cs('loanAmount')} /></div>);
    } else if (current === 1) {
        stepContent = (<div className="space-y-5"><p className="text-sm text-slate-500 dark:text-slate-400">A co-applicant (parent or guardian) is required. Their income will be considered for eligibility.</p><div className="grid gap-5 sm:grid-cols-2"><FormInput id="con" label="Co-Applicant Name" placeholder="Ramesh Verma" required {...cc('coName')} /><FormSelect id="rel" label="Relationship with Student" required options={[{ value: 'father', label: 'Father' }, { value: 'mother', label: 'Mother' }, { value: 'spouse', label: 'Spouse' }, { value: 'sibling', label: 'Sibling' }, { value: 'guardian', label: 'Legal Guardian' }]} {...cc('relationship')} /><FormInput id="ci" label="Co-Applicant Monthly Income (Rs.)" type="number" placeholder="80000" required {...cc('coIncome')} /></div></div>);
    } else if (current === 2) {
        stepContent = (<div className="grid sm:grid-cols-2 gap-4">{docs.map(function (d) { return <FileUploadField key={d.key} label={d.label} accept={d.accept} file={files[d.key]} onChange={function (e) { updFile(d.key, e.target.files[0]); }} />; })}</div>);
    } else {
        var rows = [['Student Name', student.studentName], ['Date of Birth', student.dob], ['Mobile', student.mobile], ['Email', student.email], ['Course', student.courseName], ['College', student.collegeName], ['Country', student.country], ['Duration', student.courseDuration], ['Loan Amount', student.loanAmount ? 'Rs. ' + Number(student.loanAmount).toLocaleString('en-IN') : '-'], ['Co-Applicant', co.coName], ['Relationship', co.relationship], ['Co-App. Income', co.coIncome ? 'Rs. ' + Number(co.coIncome).toLocaleString('en-IN') + ' / month' : '-']];
        stepContent = (<div className="space-y-4"><p className="text-sm text-slate-500 dark:text-slate-400">Review customer details before submitting.</p><div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden"><table className="w-full text-sm"><tbody>{rows.map(function (row, i) { return (<tr key={row[0]} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'}><td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-400 w-1/2">{row[0]}</td><td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{row[1] || '-'}</td></tr>); })}</tbody></table></div><p className="text-xs text-slate-400">By submitting you agree to our Terms and Privacy Policy.</p></div>);
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center gap-4 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                        <BookOpen className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Education Loan Application</h1>
                        <p className="text-sm text-slate-500">{roleConfig.portalLabel}</p>
                    </div>
                </div>

                {submitted ? (
                    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-8 md:p-12 text-center space-y-5 shadow-sm">
                        <div className="h-24 w-24 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center animate-bounce mx-auto">
                            <CheckCircle2 className="h-12 w-12 text-amber-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Application Filed Successfully!</h2>
                        <p className="text-slate-600 dark:text-slate-400 max-w-sm mx-auto">The <strong>Education Loan</strong> application has been recorded. You can track its status in your dashboard.</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                            <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-8"><Link to={roleConfig.loansPath}>View All Loans</Link></Button>
                            <Button variant="outline" asChild className="rounded-xl px-8"><Link to={roleConfig.dashboardPath}>Dashboard</Link></Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <ProgressBar steps={STEPS} current={current} />
                        <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 md:p-8 space-y-6">
                            <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">{STEPS[current]}</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Fields marked <span className="text-rose-500">*</span> are required.</p>
                            </div>
                            {stepContent}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={handleBack} disabled={current === 0} className="rounded-xl gap-1.5"><ChevronLeft className="h-4 w-4" />Back</Button>
                                <Button onClick={handleNext} className="rounded-xl gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-amber-600 dark:hover:bg-amber-700 text-white px-6">
                                    {isLast ? 'Submit Application' : 'Continue'}
                                    {isLast ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentEducationLoan;
